import React, { useState, useRef, useEffect, useContext, useMemo } from 'react'
import { NotificationContext } from '../../context/notification-context'
import { LangContext } from '../../context/lang-context'
import onClickAway from '../../util/onClickAway'
import useFetch from '../../hooks/fetchMSSQL-hook'
import { NotificationManager } from 'react-notifications'

export default ({ tagData, handleClose, getTags, section }) => {
  const { fetchMssqlApi } = useFetch()
  const { TRL_Pack } = useContext(LangContext)
  const { ErrorNotification } = useContext(NotificationContext)
  const modalRef = useRef(null)
  const initialTagData = useRef(tagData)

  const [options, setOptions] = useState(tagData?.options || [])
  const [removedOptions, setRemovedOptions] = useState([])
  const [input, setInput] = useState('')
  const [editedOption, setEditedOption] = useState(null)
  const [selectedSection, setSelectedSection] = useState(String(section || 1))

  const toBool = value => value === true || value === 1 || value === '1' || value === 'true'

  const isGroupActive = useMemo(() => {
    const initialOptions = initialTagData.current?.options || []
    if (!initialOptions.length) return true

    return initialOptions.every(option => toBool(option.isActive))
  }, [])

  const getSectionName = value => {
    if (Number(value) === 1) return TRL_Pack.tags.machine || 'Automat'
    if (Number(value) === 2) return TRL_Pack.tags.product || 'Produkt'

    return ''
  }

  const preventButtonFocus = evt => evt.preventDefault()

  const handleChange = evt => setInput(evt.target.value)

  const removeOption = idx => () => {
    setOptions(prev => {
      const removedOption = prev[idx]
      if (!removedOption) return prev

      setRemovedOptions(removed => removed.concat({
        option: { ...removedOption },
        index: idx
      }))

      return prev.filter((option, optionIdx) => optionIdx !== idx)
    })

    setEditedOption(prev => (
      typeof prev === 'number' && prev > idx ? prev - 1 : prev
    ))
  }

  const restoreOption = () => {
    if (!removedOptions.length || editedOption !== null) return

    const lastRemoved = removedOptions[removedOptions.length - 1]

    setOptions(prev => {
      const next = [...prev]
      const index = Math.min(lastRemoved.index, next.length)

      next.splice(index, 0, lastRemoved.option)

      return next
    })

    setRemovedOptions(prev => prev.slice(0, -1))
  }

  const saveOption = () => {
    const value = input.trim()
    if (!value) return

    if (editedOption === 'new') {
      setOptions(prev => prev.concat({ name: value, isActive: true }))
    } else {
      setOptions(prev =>
        prev.map((option, optionIdx) =>
          optionIdx === editedOption ? { ...option, name: value } : option
        )
      )
    }

    setInput('')
    setEditedOption(null)
  }

  const editOption = idx => () => {
    if (idx === null) {
      setInput('')
      setEditedOption(null)
      return
    }

    setInput(idx === 'new' ? '' : options[idx].name)
    setEditedOption(idx)
  }

  const toggleGroupActive = () => {
    const tagIds = (initialTagData.current?.options || [])
      .map(option => option.tagId)
      .filter(Boolean)

    if (!tagIds.length) return

    fetchMssqlApi(
      'tags/status',
      {
        method: 'PUT',
        hideNotification: true,
        data: { TagIds: tagIds, IsActive: !isGroupActive }
      },
      () => {
        NotificationManager.success(
          isGroupActive ? 'Dezaktywowano grupę pomyślnie' : 'Aktywowano grupę pomyślnie'
        )

        getTags()
        handleClose()
      }
    )
  }

  const handleSubmit = evt => {
    evt.preventDefault()

    const { label } = evt.target.elements
    const labelValue = label?.value?.trim()
    const promises = []
    const currentSection = tagData ? section : Number(selectedSection)

    const pushRequest = (path, method, data) => {
      promises.push(
        new Promise(resolve => {
          fetchMssqlApi(path, { method, hideNotification: true, data }, resolve)
        })
      )
    }

    if (!tagData?.others && !labelValue) return
    if (editedOption !== null) return

    if (tagData && initialTagData.current.options) {
      removedOptions
        .map(({ option }) => option?.tagId)
        .filter(Boolean)
        .forEach(tagId => pushRequest(`tag/${tagId}`, 'DELETE'))

      options.forEach(({ tagId, name }) => {
        const nextName = tagData.others ? name : `${labelValue} - ${name}`

        if (tagId) {
          const initialOption = initialTagData.current.options.find(opt => String(opt.tagId) === String(tagId))

          if (initialOption && name !== initialOption.name) {
            pushRequest(`tag/${tagId}`, 'PUT', {
              Name: nextName
            })
          }
        } else {
          pushRequest('tag', 'POST', {
            Name: nextName,
            Type: currentSection
          })
        }
      })

      if (!tagData.others && initialTagData.current.label !== labelValue) {
        pushRequest('tags', 'PUT', {
          PrevLabel: initialTagData.current.label,
          NewLabel: labelValue
        })
      }
    } else {
      if (options.length > 0) {
        options.forEach(({ name }) => {
          pushRequest('tag', 'POST', {
            Name: tagData ? name : `${labelValue} - ${name}`,
            Type: currentSection
          })
        })
      } else {
        return ErrorNotification(TRL_Pack.selectOneOption)
      }
    }

    Promise.all(promises).then(() => {
      NotificationManager.success(
        tagData ? 'Zaktualizowano grupę pomyślnie' : 'Dodano grupę pomyślnie'
      )

      getTags()
      handleClose()
    })
  }

  useEffect(
    () => onClickAway(modalRef, handleClose),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const headerText = tagData ? 'Edytuj grupę tagów' : 'Dodaj grupę tagów'

  const tagActionButtonStyle = {
    width: 42,
    minWidth: 42
  }

  const tagListStyle = {
    maxHeight: 250,
    padding: 4
  }

  const tagHeaderStyle = {
    paddingLeft: 4,
    paddingRight: 4
  }

  const tagRowStyle = {
    marginBottom: 10
  }

  const newTagRowStyle = {
    padding: 4
  }

  const selectWrapperStyle = {
    position: 'relative'
  }

  const selectStyle = {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    paddingRight: 42
  }

  const selectIconStyle = {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    fontSize: 13
  }

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div ref={modalRef} className="modal-content border-0">
          <div className="modal-header bg-light align-items-center">
            <h6 className="modal-title font-weight-bold pe-none">{headerText}</h6>
            <button
              onMouseDown={preventButtonFocus}
              onClick={handleClose}
              className="btn p-1"
            >
              <i className="fas fa-times" style={{ width: 15, height: 15 }} />
            </button>
          </div>

          <div className="modal-body p-0">
            <form id="tags-form" onSubmit={handleSubmit} className="p-3">

              <div className="form-group mb-3">
                <label className="font-weight-bold mb-2">Nazwa grupy</label>
                {tagData?.others ? (
                  <input
                    className="form-control"
                    value={TRL_Pack.tags.others || 'Inne'}
                    disabled
                    readOnly
                  />
                ) : (
                  <input
                    className="form-control"
                    name="label"
                    defaultValue={tagData?.label || ''}
                    required
                    autoComplete="off"
                  />
                )}
              </div>

              {!tagData && (
                <div className="form-group mb-3">
                  <label className="font-weight-bold mb-2">Typ</label>
                  <div style={selectWrapperStyle}>
                    <select
                      className="form-control"
                      style={selectStyle}
                      value={selectedSection}
                      onChange={evt => setSelectedSection(evt.target.value)}
                      disabled={editedOption !== null}
                      autoFocus={!!tagData}
                    >
                      <option value="1">{getSectionName(1)}</option>
                      <option value="2">{getSectionName(2)}</option>
                    </select>

                    <i className="fas fa-chevron-down" style={selectIconStyle} />
                  </div>
                </div>
              )}

              {tagData && (
                <div className="form-group mb-3">
                  <label className="font-weight-bold mb-2">Typ</label>
                  <input
                    className="form-control"
                    value={getSectionName(section)}
                    disabled
                    readOnly
                  />
                </div>
              )}

              <div className="form-group mb-0">
                <div
                  className="d-flex justify-content-between align-items-center mb-2"
                  style={tagHeaderStyle}
                >
                  <label className="font-weight-bold mb-0">Tagi</label>

                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      onMouseDown={preventButtonFocus}
                      onClick={restoreOption}
                      className="btn btn-light border mr-2"
                      style={tagActionButtonStyle}
                      disabled={editedOption !== null || !removedOptions.length}
                    >
                      <i className="fas fa-undo text-muted" />
                    </button>

                    <button
                      type="button"
                      onMouseDown={preventButtonFocus}
                      onClick={editOption('new')}
                      className="btn btn-light border"
                      style={tagActionButtonStyle}
                      disabled={editedOption !== null}
                    >
                      <i className="fas fa-plus text-muted" />
                    </button>
                  </div>
                </div>

                {options.length > 0 && (
                  <div
                    className="overflow-auto user-select-none mb-2"
                    style={tagListStyle}
                  >
                    {options.map((opt, idx) => (
                      editedOption === idx ? (
                        <div
                          key={idx}
                          className="d-flex align-items-center"
                          style={tagRowStyle}
                        >
                          <input
                            className="form-control mr-2"
                            onChange={handleChange}
                            value={input}
                            autoFocus
                          />
                          <button
                            type="button"
                            onMouseDown={preventButtonFocus}
                            className="btn btn-light border"
                            style={tagActionButtonStyle}
                            onClick={saveOption}
                            disabled={!input.trim()}
                          >
                            <i className="fas fa-check text-success" />
                          </button>
                        </div>
                      ) : (
                        <div
                          key={idx}
                          className="d-flex align-items-center"
                          style={tagRowStyle}
                        >
                          <button
                            type="button"
                            onMouseDown={preventButtonFocus}
                            className="btn btn-light border flex-fill text-left mr-2"
                            onClick={editOption(idx)}
                            disabled={editedOption !== null}
                          >
                            {opt.name}
                          </button>
                          <button
                            type="button"
                            onMouseDown={preventButtonFocus}
                            className="btn btn-light border"
                            style={tagActionButtonStyle}
                            onClick={removeOption(idx)}
                            disabled={editedOption !== null}
                          >
                            <i className="fas fa-times text-danger" />
                          </button>
                        </div>
                      )
                    ))}
                  </div>
                )}

                {editedOption === 'new' && (
                  <div
                    className="d-flex align-items-center mb-1"
                    style={newTagRowStyle}
                  >
                    <input
                      className={`form-control mr-2${input.trim() ? '' : ' invalid-input'}`}
                      onChange={handleChange}
                      value={input}
                      autoFocus
                    />
                    <button
                      type="button"
                      onMouseDown={preventButtonFocus}
                      className="btn btn-light border mr-2"
                      style={tagActionButtonStyle}
                      onClick={saveOption}
                      disabled={!input.trim()}
                    >
                      <i className="fas fa-check text-success" />
                    </button>
                    <button
                      type="button"
                      onMouseDown={preventButtonFocus}
                      className="btn btn-light border"
                      style={tagActionButtonStyle}
                      onClick={editOption(null)}
                    >
                      <i className="fas fa-times text-danger" />
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="modal-footer bg-light justify-content-between">
            <div>
              {tagData && (
                <button
                  type="button"
                  onMouseDown={preventButtonFocus}
                  className={isGroupActive ? 'btn btn-danger btn-sm' : 'btn btn-success btn-sm'}
                  onClick={toggleGroupActive}
                  disabled={!initialTagData.current?.options?.length}
                >
                  {isGroupActive ? 'Dezaktywuj' : 'Aktywuj'}
                </button>
              )}
            </div>

            <button
              type="submit"
              onMouseDown={preventButtonFocus}
              className="btn btn-primary btn-sm"
              form="tags-form"
              disabled={options.length === 0 || editedOption !== null}
            >
              {TRL_Pack.buttons.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}